import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Copy } from "lucide-react";
import { toast } from "react-toastify";

const quickstartCurl = `curl --request POST "https://your-api-host.example/api/public/v1/1234567890/messages" \\
  --header "Authorization: Bearer xb_live_xxxxxxxxxxxxxxxxxxxxxxxx" \\
  --header "Idempotency-Key: example-unique-key" \\
  --header "Content-Type: application/json" \\
  --data '{
    "messaging_product": "whatsapp",
    "to": "919876543210",
    "type": "text",
    "text": {
      "body": "Hello from Public API"
    }
  }'`;

const textRequestExample = `{
  "messaging_product": "whatsapp",
  "to": "919876543210",
  "type": "text",
  "text": {
    "body": "Hello from Public API"
  }
}`;

const templateRequestExample = `{
  "messaging_product": "whatsapp",
  "to": "919876543210",
  "type": "template",
  "template": {
    "name": "order_update",
    "language": {
      "code": "en_US"
    },
    "components": [
      {
        "type": "body",
        "parameters": [
          { "type": "text", "text": "John" },
          { "type": "text", "text": "12345" }
        ]
      }
    ]
  }
}`;

const successResponseExample = `{
  "success": true,
  "message": "Text message sent successfully.",
  "code": 200,
  "data": {
    "success": true,
    "messageId": "wamid.HBgM...",
    "logId": "11111111-2222-3333-4444-555555555555"
  },
  "errorMessage": null,
  "rawResponse": null,
  "messageId": "wamid.HBgM...",
  "logId": "11111111-2222-3333-4444-555555555555",
  "token": null,
  "refreshToken": null
}`;

const lifecyclePayloadExample = `{
  "id": "evt_124",
  "type": "message.delivered",
  "createdAt": "2026-03-26T12:35:30Z",
  "data": {
    "businessId": "aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee",
    "logId": "11111111-2222-3333-4444-555555555555",
    "messageId": "wamid.HBgM...",
    "providerMessageId": "wamid.HBgM...",
    "recipientNumber": "919876543210",
    "status": "delivered",
    "eventTimestampUtc": "2026-03-26T12:35:28Z",
    "errorMessage": null,
    "source": "public-api",
    "origin": "meta-status-webhook",
    "failureStage": null
  }
}`;

const inboundPayloadExample = `{
  "id": "evt_128",
  "type": "message.inbound.received",
  "createdAt": "2026-03-26T12:45:00Z",
  "data": {
    "businessId": "aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee",
    "logId": "66666666-7777-8888-9999-000000000000",
    "providerMessageId": "wamid.inbound.123",
    "senderNumber": "919855500001",
    "contactId": "99999999-aaaa-bbbb-cccc-dddddddddddd",
    "messageType": "text",
    "textBody": "Reply from customer",
    "eventTimestampUtc": "2026-03-26T12:44:58Z",
    "origin": "meta-webhook",
    "replyToProviderMessageId": "wamid.outbound.456"
  }
}`;

const callbackEnvelopeExample = `{
  "id": "event_id",
  "type": "message.sent",
  "createdAt": "2026-03-26T12:34:56Z",
  "data": {}
}`;

const sections = [
  { id: "overview", label: "Overview" },
  { id: "quickstart", label: "Quickstart" },
  { id: "authentication", label: "Authentication" },
  { id: "send-api", label: "Send API" },
  { id: "callback-events", label: "Callback Events" },
  { id: "callback-security", label: "Callback Security" },
  { id: "limitations", label: "Limitations" },
  { id: "workspace-setup", label: "Workspace Setup" },
];

const eventRows = [
  {
    type: "message.sent",
    description:
      "Queued after a successful outbound send, only after the message log is persisted.",
  },
  {
    type: "message.delivered",
    description:
      "Queued when a provider status webhook updates an outgoing message to delivered.",
  },
  {
    type: "message.read",
    description:
      "Queued when a provider status webhook updates an outgoing message to read.",
  },
  {
    type: "message.failed",
    description:
      "Used for send-time failures and later provider status failures. Inspect origin and failureStage.",
  },
  {
    type: "message.inbound.received",
    description:
      "Queued when a new inbound WhatsApp message is inserted through the inbox single-writer path.",
  },
];

const limitations = [
  "Public send currently supports only text and template messages.",
  "Template support is intentionally narrow: body text params, one HTTPS header image link, and URL button params only.",
  "A provider-returned send failure can still return HTTP 200 with success=false.",
  "Inbound reply correlation is currently provider-message based via replyToProviderMessageId.",
  "Lifecycle and inbound callback payloads are not perfectly uniform.",
  "Base URL is deployment-specific; relative routes are the contract source of truth.",
];

function SidebarLink({ href, label, isActive = false }) {
  return (
    <a
      href={href}
      className={`flex items-center rounded-lg border px-3 py-2 text-sm transition ${
        isActive
          ? "border-emerald-200 bg-emerald-50 text-emerald-800"
          : "border-transparent text-slate-600 hover:border-slate-200 hover:bg-slate-100 hover:text-slate-900"
      }`}
    >
      {label}
    </a>
  );
}

function MetaChip({ children }) {
  return (
    <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-medium text-slate-700">
      {children}
    </span>
  );
}

function SectionBlock({ id, eyebrow, title, description, children }) {
  return (
    <section id={id} className="scroll-mt-24">
      {eyebrow ? (
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-700">
          {eyebrow}
        </p>
      ) : null}
      <h2 className="mt-3 text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">
        {title}
      </h2>
      {description ? (
        <p className="mt-4 max-w-3xl text-[15px] leading-7 text-slate-600">
          {description}
        </p>
      ) : null}
      <div className="mt-6">{children}</div>
    </section>
  );
}

function CodeBlock({ label, code }) {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-slate-800 px-4 py-3">
        <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-400">
          {label}
        </p>
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-1 rounded-md border border-slate-700 bg-slate-900 px-2.5 py-1.5 text-xs font-medium text-slate-100 transition hover:bg-slate-800"
        >
          <Copy size={14} />
          Copy
        </button>
      </div>
      <pre className="overflow-x-auto px-4 py-4 text-xs leading-6 text-slate-100">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function InfoNote({ children, tone = "neutral" }) {
  const toneClass =
    tone === "warning"
      ? "border-amber-200 bg-amber-50 text-amber-950"
      : "border-slate-200 bg-slate-50 text-slate-700";

  return (
    <div className={`rounded-2xl border p-5 text-sm leading-6 ${toneClass}`}>
      {children}
    </div>
  );
}

function EventRow({ type, description }) {
  return (
    <div className="grid gap-2 border-b border-slate-200 py-4 last:border-b-0 md:grid-cols-[220px_minmax(0,1fr)] md:gap-6">
      <p className="text-sm font-semibold text-slate-900">
        <code>{type}</code>
      </p>
      <p className="text-sm leading-6 text-slate-600">{description}</p>
    </div>
  );
}

export default function PublicIntegrationsPage() {
  const [activeSectionId, setActiveSectionId] = useState(sections[0].id);
  const sectionLinks = useMemo(
    () => sections.map(section => ({ ...section, href: `#${section.id}` })),
    []
  );

  useEffect(() => {
    document.body.classList.remove("inbox-active");
    document.documentElement.classList.remove("inbox-active");
    document.body.style.overflow = "";
    document.documentElement.style.overflow = "";
    document.body.style.position = "";
    document.documentElement.style.position = "";
    document.body.style.top = "";
    document.documentElement.style.top = "";
    document.body.style.left = "";
    document.documentElement.style.left = "";
    document.body.style.right = "";
    document.documentElement.style.right = "";
    document.body.style.width = "";
    document.documentElement.style.width = "";
    document.body.style.height = "";
    document.documentElement.style.height = "";
    document.body.style.maxHeight = "";
    document.documentElement.style.maxHeight = "";
  }, []);

  useEffect(() => {
    const observedSections = sections
      .map(section => document.getElementById(section.id))
      .filter(Boolean);

    if (!observedSections.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      entries => {
        const visibleEntries = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visibleEntries.length > 0) {
          setActiveSectionId(visibleEntries[0].target.id);
        }
      },
      {
        rootMargin: "-20% 0px -65% 0px",
        threshold: [0.1, 0.35, 0.6],
      }
    );

    observedSections.forEach(section => observer.observe(section));

    return () => {
      observedSections.forEach(section => observer.unobserve(section));
      observer.disconnect();
    };
  }, []);

  return (
    <div className="h-screen overflow-y-auto bg-slate-50 text-slate-900">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-[60px] max-w-[1440px] items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm font-semibold tracking-tight text-slate-900">
              XploreByte Docs
            </Link>
            <span className="hidden text-sm text-slate-400 sm:inline">/</span>
            <span className="hidden text-sm text-slate-500 sm:inline">
              Integrations
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="inline-flex items-center rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
            >
              Log in
            </Link>
            <Link
              to="/signup-for-trial"
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
            >
              Start trial
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1440px] px-4 py-8 sm:px-6 lg:px-8 lg:py-10">
        <div className="mb-8 xl:hidden">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
            Sections
          </p>
          <nav className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-2">
            {sectionLinks.map(link => (
              <SidebarLink
                key={link.id}
                href={link.href}
                label={link.label}
                isActive={activeSectionId === link.id}
              />
            ))}
          </nav>
        </div>

        <div className="grid grid-cols-1 gap-10 xl:grid-cols-[250px_minmax(0,860px)] xl:justify-center xl:gap-10">
          <aside className="hidden xl:block">
            <div className="sticky top-24">
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                Sections
              </p>
              <nav className="space-y-1">
                {sectionLinks.map(link => (
                  <SidebarLink
                    key={link.id}
                    href={link.href}
                    label={link.label}
                    isActive={activeSectionId === link.id}
                  />
                ))}
              </nav>
            </div>
          </aside>

          <main className="min-w-0">
            <article className="space-y-20">
              <SectionBlock
                id="overview"
                eyebrow="Public API v1"
                title="WhatsApp integration docs for real implemented flows."
                description="Use the public send API to send WhatsApp text and template messages, then receive signed callbacks for message lifecycle events and inbound replies. This page documents the current implemented contract only."
              >
                <div className="flex flex-wrap gap-3">
                  <MetaChip>POST /api/public/v1/{"{phone_number_id}"}/messages</MetaChip>
                  <MetaChip>Bearer API key</MetaChip>
                  <MetaChip>Signed callbacks</MetaChip>
                  <MetaChip>Public API v1</MetaChip>
                </div>
                <p className="mt-6 max-w-3xl text-[15px] leading-7 text-slate-600">
                  The current implemented scope is intentionally narrow: send
                  WhatsApp <code>text</code> and <code>template</code> messages,
                  then receive lifecycle and inbound callbacks through configured
                  webhook endpoints.
                </p>
              </SectionBlock>

              <SectionBlock
                id="quickstart"
                eyebrow="Quickstart"
                title="Three steps to go live"
                description="Keep the first implementation path simple: generate an API key, send one message, and subscribe webhook endpoints for signed delivery events."
              >
                <ol className="space-y-3 text-sm leading-7 text-slate-600">
                  <li>
                    <span className="font-semibold text-slate-900">1.</span> Create an
                    API key in your workspace.
                  </li>
                  <li>
                    <span className="font-semibold text-slate-900">2.</span> Send your
                    first request to{" "}
                    <code>/api/public/v1/{"{phone_number_id}"}/messages</code>.
                  </li>
                  <li>
                    <span className="font-semibold text-slate-900">3.</span> Configure
                    webhook endpoints for signed callback delivery.
                  </li>
                </ol>
                <div className="mt-6">
                  <CodeBlock
                    label="Example cURL (replace the sample host)"
                    code={quickstartCurl}
                  />
                </div>
                <div className="mt-4">
                  <InfoNote>
                    This page mirrors the currently implemented contract only.
                    Use a deployment-specific host, but keep the relative route
                    shape unchanged.
                  </InfoNote>
                </div>
              </SectionBlock>

              <SectionBlock
                id="authentication"
                eyebrow="Authentication"
                title="Use a bearer API key"
                description="Public API requests use a workspace-scoped API key. Keys currently use the xb_live_ prefix, and only the key hash is stored server-side."
              >
                <div className="space-y-4">
                  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                    <code>Authorization: Bearer &lt;api_key&gt;</code>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                    <code>Idempotency-Key: &lt;unique-request-key&gt;</code>{" "}
                    <span className="text-slate-500">(optional, recommended)</span>
                  </div>
                  <InfoNote>
                    Keys currently use the format{" "}
                    <code>xb_live_&lt;random suffix&gt;</code>. Raw key material is
                    shown once at creation time, and only the hash is stored
                    server-side.
                  </InfoNote>
                  <InfoNote>
                    <code>Idempotency-Key</code> is optional but recommended for
                    production integrations and must not exceed 200 characters.
                    Without it, the request still sends normally but duplicate
                    protection is not applied. Use a new key for each new message,
                    and reuse it only when retrying the exact same request.
                  </InfoNote>
                </div>
              </SectionBlock>

              <SectionBlock
                id="send-api"
                eyebrow="Send API"
                title="Send text and template messages"
                description="The current public send surface is intentionally narrow and stable: text and template only."
              >
                <div className="space-y-4 text-[15px] leading-7 text-slate-600">
                  <p>
                    Route:{" "}
                    <code>POST /api/public/v1/{"{phone_number_id}"}/messages</code>
                  </p>
                  <p>
                    Supported message types: <code>text</code>, <code>template</code>
                  </p>
                </div>

                <div className="mt-6 grid gap-6 xl:grid-cols-2">
                  <CodeBlock label="Text request" code={textRequestExample} />
                  <CodeBlock label="Template request" code={templateRequestExample} />
                </div>

                <div className="mt-6">
                  <CodeBlock
                    label="Successful send response"
                    code={successResponseExample}
                  />
                </div>

                <div className="mt-6 grid gap-4 lg:grid-cols-[1.05fr_0.95fr]">
                  <InfoNote>
                    <p className="font-semibold text-slate-900">
                      Validation highlights
                    </p>
                    <ul className="mt-3 space-y-2">
                      <li>
                        <code>to</code> is required.
                      </li>
                      <li>
                        <code>type</code> must be <code>text</code> or{" "}
                        <code>template</code>.
                      </li>
                      <li>
                        <code>messaging_product</code>, if provided, must be{" "}
                        <code>whatsapp</code>.
                      </li>
                      <li>
                        <code>phone_number_id</code> is required in the route and
                        must not be sent in the body.
                      </li>
                      <li>
                        <code>provider</code> must not be sent; it is resolved
                        from the selected sender.
                      </li>
                      <li>Template validation is intentionally strict and narrow.</li>
                    </ul>
                  </InfoNote>
                  <InfoNote tone="warning">
                    <p className="font-semibold">Current provider-failure behavior</p>
                    <p className="mt-2">
                      If the provider returns a non-success result without throwing
                      an exception, the API can still return HTTP <code>200</code> with{" "}
                      <code>success=false</code>. The failed message log is still
                      persisted and <code>message.failed</code> is still queued.
                    </p>
                  </InfoNote>
                </div>
              </SectionBlock>

              <SectionBlock
                id="callback-events"
                eyebrow="Callback Events"
                title="Lifecycle and inbound callbacks"
                description="All callbacks use the same envelope shape: id, type, createdAt, and data. Subscribe endpoints per event based on the workflow you need."
              >
                <div className="mb-6">
                  <CodeBlock
                    label="Callback envelope"
                    code={callbackEnvelopeExample}
                  />
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white">
                  <div className="px-5 py-2">
                    {eventRows.map(event => (
                      <EventRow
                        key={event.type}
                        type={event.type}
                        description={event.description}
                      />
                    ))}
                  </div>
                </div>
                <div className="mt-6 grid gap-6 xl:grid-cols-2">
                  <CodeBlock
                    label="Lifecycle callback example"
                    code={lifecyclePayloadExample}
                  />
                  <CodeBlock
                    label="Inbound callback example"
                    code={inboundPayloadExample}
                  />
                </div>
              </SectionBlock>

              <SectionBlock
                id="callback-security"
                eyebrow="Callback Security"
                title="Signed delivery with retries"
                description="Webhook delivery is signed per endpoint and retried automatically. Consumers should treat deliveries as at-least-once and verify signatures against the raw body."
              >
                <div className="space-y-6">
                  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                    <code>
                      X-XploreByte-Signature: t=&lt;unix_ts&gt;,v1=&lt;hex_hmac_sha256&gt;
                    </code>
                  </div>
                  <p className="text-[15px] leading-7 text-slate-600">
                    The signature is calculated over{" "}
                    <code>&lt;t&gt;.&lt;raw_body&gt;</code>. Each business can
                    configure multiple active webhook endpoints, and delivery is
                    fanned out per subscribed event name.
                  </p>
                  <InfoNote>
                    <ul className="space-y-2">
                      <li>5-second request timeout per attempt.</li>
                      <li>Retries on timeout or non-2xx responses.</li>
                      <li>Deliveries are at-least-once.</li>
                      <li>Consumers should be idempotent.</li>
                      <li>Production webhook URLs must use HTTPS and resolve only to public IP addresses.</li>
                      <li>Redirects are not followed, and DNS is revalidated before delivery.</li>
                    </ul>
                  </InfoNote>
                </div>
              </SectionBlock>

              <SectionBlock
                id="limitations"
                eyebrow="Current Limitations"
                title="Documented boundaries of the current contract"
                description="These are current implementation boundaries, not roadmap statements."
              >
                <InfoNote tone="warning">
                  <ul className="space-y-3">
                    {limitations.map(item => (
                      <li key={item} className="flex gap-3">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-amber-500" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </InfoNote>
              </SectionBlock>

              <SectionBlock
                id="workspace-setup"
                eyebrow="Workspace Setup"
                title="Create a workspace and wire your first endpoint"
                description="Use the existing public signup flow to generate API keys, configure webhook endpoints, and start with the current Public API v1 contract."
              >
                <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-6 sm:flex-row sm:items-center sm:justify-between">
                  <p className="max-w-2xl text-sm leading-6 text-slate-600">
                    API keys and webhook endpoints are managed in your workspace.
                    The public docs page stays aligned to the implemented
                    contract, while the product handles the operational setup.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Link
                      to="/signup-for-trial"
                      className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800"
                    >
                      Start trial
                      <ArrowRight size={14} />
                    </Link>
                    <Link
                      to="/login"
                      className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                    >
                      Log in
                    </Link>
                  </div>
                </div>
              </SectionBlock>
            </article>
          </main>
        </div>
      </div>
    </div>
  );
}
